import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import {
  PageContainer,
  Container,
  Section,
  PageTitle,
  SectionTitle,
  ContentText,
  LeftAlignedContentText,
  LazyBackgroundImage,
  LazyImage,
  BulletCardsGrid,
  BulletCard,
  BulletCardIcon,
  BulletCardText,
  CheckIcon,
  heroStaggerContainerVariants,
  heroStaggerItemVariants,
  ContactButton,
} from '../components/shared/SharedComponents';
import { useContent } from '../hooks/useContent';
import { useNavigationAwareAnimations } from '../hooks/useNavigationAwareAnimations';
import { useScrollToTop } from '../hooks/useScrollToTop';

const GovernanceDividerSection = styled(Section)`
  padding: 30px 0;
  position: relative;

  &:not(:last-of-type)::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(0, 0, 0, 0.1) 50%,
      transparent 100%
    );
  }
`;

const IntroHeadline = styled.h2`
  font-size: clamp(20px, 2.8vw, 26px);
  font-weight: 800;
  color: #1d334e;
  text-align: left;
  margin: 0 0 24px;
  max-width: 900px;
  line-height: 1.25;
  letter-spacing: -0.02em;
`;

const SectionIntro = styled.p`
  font-size: 16px;
  color: rgba(29, 51, 78, 0.85);
  font-weight: 500;
  margin-bottom: 20px;
  margin-top: 0;
  line-height: 1.6;
  text-align: left;
`;

const SectionClosing = styled.p`
  font-size: 16px;
  color: rgba(29, 51, 78, 0.8);
  margin-top: 24px;
  margin-bottom: 0;
  line-height: 1.65;
  text-align: left;
`;

const CenteredSectionIntro = styled(SectionIntro)`
  text-align: center;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;

const ThreeColumnBulletGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 0;
  margin-bottom: 0;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CenteredSectionClosing = styled(SectionClosing)`
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const HeroImageSection = styled.section`
  margin-bottom: 48px;
  border-radius: 24px;
  overflow: hidden;
  height: 320px;
  min-height: 320px;

  @media (max-width: 768px) {
    height: 220px;
    min-height: 220px;
    margin-bottom: 32px;
  }

  > div {
    height: 100%;
    min-height: inherit;
  }
`;

const SectionImageWrapper = styled.section`
  margin: 48px 0;
  border-radius: 24px;
  overflow: hidden;
  height: 280px;
  min-height: 280px;

  @media (max-width: 768px) {
    height: 200px;
    min-height: 200px;
    margin: 32px 0;
  }

  > div {
    height: 100%;
    min-height: inherit;
  }
`;

const ThreeColumnImageWrapper = styled(SectionImageWrapper)`
  margin-top: 0;
`;

/* Our Role section: title+intro above, image left | bullets right, closing below */
const OurRoleHeader = styled.div`
  margin-bottom: 32px;
`;
const OurRoleColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: stretch;
  margin: 32px 0 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;
const OurRoleImageCol = styled.div`
  min-height: 300px;
  display: flex;
  min-width: 0;

  @media (max-width: 968px) {
    min-height: 250px;
    order: -1;
  }

  [data-swap='true'] & {
    @media (max-width: 968px) {
      order: 1;
    }
  }
`;
const OurRoleBulletsCol = styled.div`
  margin: 0;
  min-width: 0;

  [data-swap='true'] & {
    @media (max-width: 968px) {
      order: -1;
    }
  }
`;

const OurRoleImageWrapper = styled.div`
  flex: 1;
  height: 100%;
  min-height: 280px;
  border-radius: 24px;
  overflow: hidden;
  position: relative;

  @media (max-width: 968px) {
    min-height: 220px;
  }

  > div {
    height: 100%;
    min-height: inherit;
  }
`;
const OurRoleClosing = styled.div`
  margin-top: 0;
`;

const ContentSection = styled.div`
  padding: 0;
`;

function ExecutionGovernance() {
  useScrollToTop();
  useNavigationAwareAnimations();
  const { getPageContent, footerInfo, contactInfo, site } = useContent();
  const page = getPageContent('executionGovernance');

  if (!page.sections && page.description) {
    return (
      <>
        <SEO title={page.title} description={page.description} />
        <PageContainer>
          <Container>
            <PageTitle className="with-underline">{page.title}</PageTitle>
            <ContentText>
              <p>{page.description}</p>
            </ContentText>
          </Container>
        </PageContainer>
      </>
    );
  }

  const { title, subtitle, heroImage, intro, sections, cta } = page;
  const metaDescription =
    subtitle ||
    intro?.headline ||
    'Institutional oversight for cross-border industrial trade, supply chains, and execution governance.';

  return (
    <>
      <SEO title={title || 'Execution Governance'} description={metaDescription} />
      <StructuredData
        type="WebPage"
        data={{
          page: {
            title: title || 'Execution Governance',
            description: metaDescription,
            url: '/execution-governance',
          },
          organization: { footerInfo, contactInfo },
          site,
        }}
        includeOrganization={true}
      />
      <PageContainer>
        <Container>
          <article>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={heroStaggerContainerVariants}
            >
              <PageTitle className="with-underline" variants={heroStaggerItemVariants}>
                {title}
              </PageTitle>
              {subtitle && (
                <motion.p
                  variants={heroStaggerItemVariants}
                  style={{
                    fontSize: 'clamp(16px, 2.5vw, 20px)',
                    color: 'rgba(29, 51, 78, 0.8)',
                    textAlign: 'center',
                    maxWidth: 800,
                    margin: '0 auto 40px',
                    lineHeight: 1.6,
                  }}
                >
                  {subtitle}
                </motion.p>
              )}
            </motion.div>

            {heroImage && (
              <HeroImageSection>
                <LazyBackgroundImage
                  src={heroImage}
                  aria-label={title}
                  critical
                  style={{ height: '100%', minHeight: '100%' }}
                >
                  <div style={{ height: '100%', minHeight: '100%' }} />
                </LazyBackgroundImage>
              </HeroImageSection>
            )}

            {(intro?.headline || (intro?.paragraphs && intro.paragraphs.length > 0)) && (
              <GovernanceDividerSection>
                {intro.headline && <IntroHeadline>{intro.headline}</IntroHeadline>}
                {intro?.paragraphs && intro.paragraphs.length > 0 && (
                  <LeftAlignedContentText
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    {intro.paragraphs.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </LeftAlignedContentText>
                )}
              </GovernanceDividerSection>
            )}

            {sections?.map((section, index) => {
              const useImageTextLayout =
                section.title === 'Our Role in Execution Governance' ||
                section.title === '我们在执行治理中的角色' ||
                section.title === 'Integrated Across All Industrial Sectors' ||
                section.title === '全面覆盖各工业领域';
              const swapColumns =
                section.title === 'Integrated Across All Industrial Sectors' ||
                section.title === '全面覆盖各工业领域';
              const hasImage = !!section.image;
              const isThreeColumn = section.layout === 'threeColumn';

              if (isThreeColumn && section.bullets?.length > 0) {
                return (
                  <GovernanceDividerSection key={index}>
                    <ContentSection>
                      <SectionTitle
                        as={motion.h2}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                        viewport={{ once: true }}
                        className="with-underline"
                      >
                        {section.title}
                      </SectionTitle>
                      {section.image && (
                        <ThreeColumnImageWrapper>
                          <LazyImage
                            src={section.image}
                            alt={section.title}
                            height="100%"
                            objectFit="cover"
                            critical
                          />
                        </ThreeColumnImageWrapper>
                      )}
                      {section.intro && <SectionIntro>{section.intro}</SectionIntro>}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
                        viewport={{ once: true }}
                      >
                        <ThreeColumnBulletGrid>
                          {section.bullets.map((bullet, bulletIndex) => (
                            <BulletCard key={bulletIndex}>
                              <BulletCardIcon>
                                <CheckIcon />
                              </BulletCardIcon>
                              <BulletCardText>{bullet}</BulletCardText>
                            </BulletCard>
                          ))}
                        </ThreeColumnBulletGrid>
                      </motion.div>
                      {section.closing && <SectionClosing>{section.closing}</SectionClosing>}
                    </ContentSection>
                  </GovernanceDividerSection>
                );
              }

              const contentBlock = (
                <>
                  <SectionTitle
                    as={motion.h2}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                    className="with-underline"
                  >
                    {section.title}
                  </SectionTitle>
                  {section.intro && <CenteredSectionIntro>{section.intro}</CenteredSectionIntro>}
                  {section.bullets && section.bullets.length > 0 && (
                    <BulletCardsGrid style={{ marginTop: 16, marginBottom: 0 }}>
                      {section.bullets.map((bullet, bulletIndex) => (
                        <BulletCard key={bulletIndex}>
                          <BulletCardIcon>
                            <CheckIcon />
                          </BulletCardIcon>
                          <BulletCardText>{bullet}</BulletCardText>
                        </BulletCard>
                      ))}
                    </BulletCardsGrid>
                  )}
                  {section.closing && (
                    <CenteredSectionClosing>{section.closing}</CenteredSectionClosing>
                  )}
                </>
              );

              if (useImageTextLayout && hasImage) {
                return (
                  <GovernanceDividerSection key={index}>
                    <ContentSection>
                      <OurRoleHeader
                        as={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                        viewport={{ once: true }}
                      >
                        <SectionTitle className="with-underline">{section.title}</SectionTitle>
                        {section.intro && <SectionIntro>{section.intro}</SectionIntro>}
                      </OurRoleHeader>
                      <OurRoleColumns
                        as={motion.div}
                        data-swap={swapColumns ? 'true' : undefined}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                        viewport={{ once: true }}
                      >
                        {swapColumns ? (
                          <>
                            <OurRoleBulletsCol>
                              {section.bullets && section.bullets.length > 0 && (
                                <BulletCardsGrid style={{ marginTop: 0, marginBottom: 0 }}>
                                  {section.bullets.map((bullet, bulletIndex) => (
                                    <BulletCard key={bulletIndex}>
                                      <BulletCardIcon>
                                        <CheckIcon />
                                      </BulletCardIcon>
                                      <BulletCardText>{bullet}</BulletCardText>
                                    </BulletCard>
                                  ))}
                                </BulletCardsGrid>
                              )}
                            </OurRoleBulletsCol>
                            <OurRoleImageCol>
                              <OurRoleImageWrapper>
                                <LazyImage
                                  src={section.image}
                                  alt={section.title}
                                  height="100%"
                                  objectFit="cover"
                                  critical
                                />
                              </OurRoleImageWrapper>
                            </OurRoleImageCol>
                          </>
                        ) : (
                          <>
                            <OurRoleImageCol>
                              <OurRoleImageWrapper>
                                <LazyImage
                                  src={section.image}
                                  alt={section.title}
                                  height="100%"
                                  objectFit="cover"
                                  critical
                                />
                              </OurRoleImageWrapper>
                            </OurRoleImageCol>
                            <OurRoleBulletsCol>
                              {section.bullets && section.bullets.length > 0 && (
                                <BulletCardsGrid style={{ marginTop: 0, marginBottom: 0 }}>
                                  {section.bullets.map((bullet, bulletIndex) => (
                                    <BulletCard key={bulletIndex}>
                                      <BulletCardIcon>
                                        <CheckIcon />
                                      </BulletCardIcon>
                                      <BulletCardText>{bullet}</BulletCardText>
                                    </BulletCard>
                                  ))}
                                </BulletCardsGrid>
                              )}
                            </OurRoleBulletsCol>
                          </>
                        )}
                      </OurRoleColumns>
                      {section.closing && (
                        <OurRoleClosing
                          as={motion.div}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
                          viewport={{ once: true }}
                        >
                          <SectionClosing>{section.closing}</SectionClosing>
                        </OurRoleClosing>
                      )}
                    </ContentSection>
                  </GovernanceDividerSection>
                );
              }

              const imageBlockFullWidth = hasImage && (
                <SectionImageWrapper>
                  <LazyBackgroundImage
                    src={section.image}
                    aria-label={section.title}
                    style={{ height: '100%', minHeight: '100%' }}
                  >
                    <div style={{ height: '100%', minHeight: '100%' }} />
                  </LazyBackgroundImage>
                </SectionImageWrapper>
              );
              const contentBlockEl = (
                <ContentSection>
                  <LeftAlignedContentText
                    as={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    {contentBlock}
                  </LeftAlignedContentText>
                </ContentSection>
              );
              const imageAfter = section.imageAfter === true;
              return (
                <GovernanceDividerSection key={index}>
                  {hasImage && !imageAfter && imageBlockFullWidth}
                  {contentBlockEl}
                  {hasImage && imageAfter && imageBlockFullWidth}
                </GovernanceDividerSection>
              );
            })}

            {cta?.buttonText && (
              <Section style={{ textAlign: 'center', padding: '60px 0' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  <ContactButton to={cta.buttonLink || '/contact'}>{cta.buttonText}</ContactButton>
                </motion.div>
              </Section>
            )}
          </article>
        </Container>
      </PageContainer>
    </>
  );
}

export default ExecutionGovernance;
